<template>
  <div class="person-browser">
    <!-- Search -->
    <div class="search-section mb-3">
      <div class="input-group">
        <input
          type="text"
          class="form-control"
          :placeholder="$t('searchParty')"
          v-model="searchQuery"
          @input="onSearchInput"
        />
        <div class="input-group-append">
          <button class="btn btn-outline-secondary" type="button">
            <i class="fa fa-search"></i>
          </button>
          <button class="btn btn-outline-secondary" type="button" @click="clearSearch">
            <i class="fa fa-remove"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Results info -->
    <div class="results-info mb-2">
      <small class="text-muted">{{ totalParties }} {{ $t('results') }}</small>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-4">
      <i class="fa fa-spinner fa-spin"></i> {{ $t('loading') }}
    </div>

    <!-- Party List -->
    <div v-else-if="parties.length > 0" class="person-browser-content">
      <div
        v-for="party in parties"
        :key="party._id"
        class="party-row d-flex align-items-center justify-content-between py-2 px-2"
      >
        <div class="party-info">
          <strong>{{ partyDisplayName(party) }}</strong>
          <span v-if="party.city" class="text-muted ml-2">{{ party.zip }} {{ party.city }}</span>
          <span v-if="party.email" class="text-muted ml-2">
            <i class="fa fa-envelope fa-sm"></i> {{ party.email }}
          </span>
        </div>
        <button
          class="btn btn-sm btn-success"
          @click="selectParty(party)"
          :disabled="isAlreadyAssigned(party)"
          :title="isAlreadyAssigned(party) ? $t('personAlreadyAssigned') : ''"
        >
          <i class="fa fa-plus"></i>
        </button>
      </div>

      <!-- Load More -->
      <div v-if="hasMoreParties" class="text-center py-3">
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="loadMoreParties"
          :disabled="loadingMore"
        >
          <i v-if="loadingMore" class="fa fa-spinner fa-spin mr-1"></i>
          {{ $t('load_more') }}
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-muted text-center py-4">
      <i class="fa fa-users fa-2x mb-2 d-block"></i>
      {{ $t('no_results') }}
    </div>

    <Toast />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Toast from 'primevue/toast';

export default {
  name: 'PersonBrowser',
  components: {
    Toast,
  },
  props: {
    /** Project the parties are searched for; the search checks its block project-parties. */
    projectNumber: {
      type: Number,
      required: true,
    },
    eligibleFor: {
      type: String,
      required: true,
    },
    excludeIds: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      loadingMore: false,
      initialPageSize: 12,
      loadMorePageSize: 8,
      currentPageSize: 0,
      parties: [],
      totalParties: 0,
      personBrowserTableStateConfig: {
        name: `personBrowser_${this.eligibleFor}`,
        filters: {
          partySearch: { value: null },
        },
        customFilters: {},
        sorting: { sortField: 'lastname', sortOrder: 1 },
        pagination: { page: 0, pageSize: 15 },
      },
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'getTableState']),

    tableStateStore() {
      return this.getTableState(this.personBrowserTableStateConfig.name);
    },
    hasMoreParties() {
      return this.parties.length < this.totalParties;
    },
  },
  methods: {
    ...mapActions(['fetchProjectPartyCandidates', 'initTableState']),

    partyDisplayName(party) {
      const personName = [party.firstname, party.lastname]
        .filter(Boolean)
        .join(' ')
        .trim();
      const company = party.company?.trim() || '';

      if (company && personName) {
        return `${company} - ${personName}`;
      }
      if (personName) {
        return personName;
      }
      if (company) {
        return company;
      }
      return '-';
    },

    isAlreadyAssigned(party) {
      return this.excludeIds.includes(party._id);
    },

    selectParty(party) {
      if (this.isAlreadyAssigned(party)) {
        return;
      }
      this.$emit('select', party);
      this.$toast.add({
        severity: 'success',
        summary: this.$t('SUCCESSFUL'),
        detail: `${this.partyDisplayName(party)} ${this.$t('added')}`,
        life: 3000,
      });
    },

    /**
     * Load remote table data with current page size
     */
    async loadTableData() {
      const { parties, partyCount } = await this.fetchProjectPartyCandidates({
        projectNumber: this.projectNumber,
        eligibleFor: [this.eligibleFor],
        search: this.tableStateStore.filters.partySearch.value,
        pagination: {
          skip: 0,
          pageSize: this.currentPageSize,
        },
      });
      this.parties = parties;
      this.totalParties = partyCount;
    },

    /**
     * Initialize/reset data loading (for new searches/filters)
     */
    async initializeData() {
      this.currentPageSize = this.initialPageSize;
      await this.loadTableData();
    },

    /**
     * Load more parties (for load more button)
     */
    async loadMoreParties() {
      if (this.loadingMore || !this.hasMoreParties) return;

      this.loadingMore = true;
      try {
        this.currentPageSize += this.loadMorePageSize;
        await this.loadTableData();
      } finally {
        this.loadingMore = false;
      }
    },

    /**
     * Handle search input with debouncing
     */
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.tableStateStore.filters.partySearch.value = this.searchQuery || null;
        this.initializeData();
      }, 300);
    },

    clearSearch() {
      this.searchQuery = '';
      this.tableStateStore.filters.partySearch.value = null;
      this.initializeData();
    },
  },

  async created() {
    await this.initTableState(this.personBrowserTableStateConfig);
  },

  async mounted() {
    await this.initializeData();
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped lang="scss">
.person-browser {
  display: flex;
  flex-direction: column;
  height: 100%;

  .search-section {
    flex-shrink: 0;
  }

  .person-browser-content {
    flex-grow: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.party-row {
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
}

.party-info {
  min-height: 32px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
